import { DIFFICULTY_LEVELS } from '../../utils/difficultyLevels';

interface AssignDifficultyControlsProps {
  /** Current persisted level (null when unrated). The matching button is highlighted. */
  currentLevel: number | null;
  /** True while an Assign is in flight, the auto-advance window is open, or the set is loading. */
  disabled: boolean;
  /** Set by the page after a successful Assign; cleared on filter / nav / refresh. */
  confirmationMessage: string | null;
  /** Set by the page when an Assign call fails; cleared on next Assign attempt. */
  errorMessage: string | null;
  /** Called with the clicked level. */
  onAssign: (level: number) => void;
}

// Action-row controls for the Overcalling Assign Level of Difficulty page (FR-009).
// Session 2026-05-29: replaced the dropdown + Assign button with a row of 1-10 buttons.
// Clicking a button assigns that level immediately and the page auto-advances to the next
// hand — there is no separate confirm step. The button matching the hand's persisted level is
// highlighted so navigating back to an already-rated hand shows its current rating (Story 2).
// Introduced by feature 012-overcalling-assign-difficulty.
export function AssignDifficultyControls({
  currentLevel,
  disabled,
  confirmationMessage,
  errorMessage,
  onAssign,
}: AssignDifficultyControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-ink">Level of difficulty</span>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Level of difficulty">
        {DIFFICULTY_LEVELS.map(level => {
          const isCurrent = currentLevel === level;
          const stateClasses = disabled
            ? isCurrent
              ? 'bg-primary-200 text-primary-500 border-primary-200 cursor-not-allowed'
              : 'bg-cream text-ink-soft border-line cursor-not-allowed'
            : isCurrent
              ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
              : 'bg-card text-ink border-line-strong hover:bg-primary-50 hover:border-primary-400';
          return (
            <button
              key={level}
              type="button"
              onClick={() => onAssign(level)}
              disabled={disabled}
              aria-pressed={isCurrent}
              aria-label={`Assign level of difficulty ${level}`}
              data-testid={`difficulty-level-${level}`}
              className={`w-9 h-9 short:w-8 short:h-8 rounded-md text-sm font-semibold border ${stateClasses}`}
            >
              {level}
            </button>
          );
        })}
      </div>
      {confirmationMessage && (
        <span className="text-green-700 font-medium text-sm" data-testid="assign-difficulty-confirmation">
          {confirmationMessage}
        </span>
      )}
      {errorMessage && (
        <span className="text-red-700 font-medium text-sm" data-testid="assign-difficulty-error">
          {errorMessage}
        </span>
      )}
    </div>
  );
}
